import { useEffect, useMemo, useState } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { useAccessToken } from '../auth/use-access-token.js';
import { apiFetch, ApiError, NotSignedIn } from './client.js';

/**
 * A coach's own lessons for one day — his book of work.
 *
 * ONE READ FOR BOTH SCREENS. The day list and a lesson's own record are one
 * push apart, so the record reads this rather than a route of its own. Same
 * shape the parent's lessons list already uses, and for the same reason: a
 * second route would return the same fields and drift from them.
 *
 * WHOSE LESSONS IS THE API'S ANSWER, NOT THIS FILE'S. It filters on
 * lesson.coach_id, which checkout wrote from the availability block the hour
 * was published from. A coach sees only lessons in hours he added.
 */

export interface DayAttendee {
  client_id: string;
  name: string;
}

/**
 * One athlete's lesson record (migration 012). Two siblings, or two families in
 * a group, are two of these sharing a `slot_id`.
 */
export interface CoachLesson {
  lesson_id: string;
  booking_id: string;
  /** The hour. What puts one hour's athletes back together on the day list. */
  slot_id: string;
  status: string;
  slot_start: string;
  slot_end: string;
  product_name: string | null;
  /** The clinic this athlete is in (033). */
  clinic_id: string | null;
  /** Credits are scoped to the service, so the closing controls need both. */
  service_id: string;
  service_name: string;
  facility_name: string;
  resource_name: string;
  /** Credits belong to the account, not the athlete. */
  adult_account_id: string;
  account_name: string;
  notes: string | null;
  practice_plan: string | null;
  /** Null while the lesson is open. A lesson closes once. */
  closure: { reason: string | null; creditUsed: boolean } | null;
  /** The athlete this record is about. */
  athlete: DayAttendee;
  /** Null unless this athlete's lesson was cancelled. It stays on the day. */
  cancellation: {
    by: 'customer' | 'coach';
    byName: string | null;
    at: string;
    message: string | null;
  } | null;
  /** Places still free in the hour, or null while it was never claimed. */
  places_free: number | null;
  /** The family has not signed up yet: where the invitation went, and when (stage C). */
  invitation: { email: string; sent_at: string | null } | null;
  /** Swings Recul counted (027); null while it never counted here. */
  swing_count: number | null;
  /** The parent said Yes to photos and video: Recul offers Save in lesson only then. */
  media_consent: boolean;
  /** A parent shares this athlete's lessons with the team coaches (030): the mark on the Athlete line. */
  shared_with_team_coaches: boolean;
}

export type CoachDayState =
  | { status: 'loading' }
  | { status: 'ready'; lessons: CoachLesson[] }
  | { status: 'failed'; message: string };

export function useCoachDay(day: Date): { state: CoachDayState; reload: () => void } {
  const isAuthenticated = useIsAuthenticated();
  const getToken = useAccessToken();
  const [state, setState] = useState<CoachDayState>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);

  // Midnight to midnight in the viewer's own clock. Which lessons fall inside
  // it is the server's answer; the window is only how far to ask.
  const { from, to } = useMemo(() => {
    const start = new Date(day);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 1);
    return { from: start.toISOString(), to: end.toISOString() };
  }, [day]);

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;
    setState({ status: 'loading' });

    const query = new URLSearchParams({ from, to });

    apiFetch<{ lessons: CoachLesson[] }>(`/api/coach/lessons?${query.toString()}`, getToken)
      .then((body) => {
        if (!cancelled) setState({ status: 'ready', lessons: body.lessons });
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        if (cause instanceof NotSignedIn) return;
        setState({
          status: 'failed',
          message:
            cause instanceof ApiError
              ? cause.message
              : 'The API did not answer. Is the Functions host running on port 7071?',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, getToken, from, to, nonce]);

  return { state, reload: () => setNonce((n) => n + 1) };
}

/** What a closed lesson says on the row: the reason, or that it was completed. */
export function closureLabel(lesson: CoachLesson): string | null {
  if (lesson.closure === null) return null;
  if (lesson.closure.creditUsed) return lesson.clinic_id ? 'Checked in' : 'Completed';

  const reason = lesson.closure.reason;
  if (reason === null) return 'Closed';
  if (reason === 'no_show') return 'No show';
  if (reason === 'cancellation') return 'Cancelled';
  return reason.charAt(0).toUpperCase() + reason.slice(1);
}
